/**
 * The drawing gesture, as a state machine — press-drag-release *and*
 * click-move-click, from one set of rules.
 *
 * ## Why both, rather than a choice
 *
 * The app already had both, inconsistently. A built-in tool (Trend line, Line
 * segment, box, fib) was press-drag-release; the `scichart-financial-tools` tools
 * in the *same palette group* (Extended line, Angle line, Polyline) place a point
 * per click, because that is what the library's placement modifier does. So
 * "Trend line ╱" dragged and "Extended line ↗" clicked, one menu apart.
 *
 * Drag is what desktop platforms use (MetaTrader, Thinkorswim). Click-move-click
 * is what TradingView uses, which is the muscle memory most people arrive with,
 * and it is the better gesture for the thing being drawn here: a trend line
 * crosses most of the chart, and holding a button down while traversing it is
 * awkward with a mouse and worse with a trackpad. It also lets the second point
 * be adjusted before it is committed, where a drag commits wherever the finger
 * happened to lift.
 *
 * TradingView accepts both, which is why nobody there notices the difference.
 * That is what this does, and the release decides which one happened: a press
 * that travelled becomes a drag and commits; a press that did not is a *click*,
 * and the gesture stays live waiting for a second one.
 *
 * ## Why it is here and not in the modifier
 *
 * `drawing-modifier.ts` says it plainly: SciChart cannot be imported under
 * jsdom, so anything living there "is verified by eye or not at all". A
 * two-phase machine with a click threshold is exactly the kind of thing that
 * needs tests, so the modifier stays the pixel converter it advertises itself as
 * and this holds the rules.
 */

import type { DrawPoint } from "./annotation-draw.js";

/**
 * How far a press may travel and still be a click, in device pixels.
 *
 * Generous rather than exact. A "click" from a hand resting on a mouse moves a
 * pixel or two, and a trackpad tap can move three or four while the finger
 * flattens — so a threshold of one would turn most taps into one-pixel drags,
 * which `isWorthKeeping` then discards, and the drawing would simply never
 * appear. Too generous is the safer error: a short *intended* drag that lands
 * inside the threshold becomes a click and waits for a second one, which the
 * user can see and finish.
 */
export const CLICK_SLOP_PX = 4;

/** Whether a press that ended here counts as a click rather than a drag. */
export function isClick(from: { x: number; y: number }, to: { x: number; y: number }): boolean {
  return Math.abs(to.x - from.x) <= CLICK_SLOP_PX && Math.abs(to.y - from.y) <= CLICK_SLOP_PX;
}

/**
 * Where a gesture is.
 *
 * `drag` is a button held down. `place` is the state a click leaves behind: the
 * first point is fixed, the rubber band follows the pointer with no button held,
 * and the next press finishes it.
 */
export type DrawGesture =
  | { phase: "idle" }
  | { phase: "drag"; start: DrawPoint; path: DrawPoint[] }
  | { phase: "place"; start: DrawPoint };

export const IDLE: DrawGesture = { phase: "idle" };

/** What the caller should do about it. The machine itself touches nothing. */
export type DrawEffect =
  | { do: "none" }
  | { do: "preview"; start: DrawPoint; current: DrawPoint; path: DrawPoint[] }
  | { do: "commit"; start: DrawPoint; end: DrawPoint; path: DrawPoint[] };

export type DrawEvent =
  | { type: "down"; at: DrawPoint }
  | { type: "move"; at: DrawPoint }
  /** `click` is the modifier's own pixel measurement — see {@link isClick}. */
  | { type: "up"; at: DrawPoint; click: boolean }
  | { type: "cancel" };

const NOTHING = { do: "none" } as const;

/**
 * Advance the gesture.
 *
 * `freehand` is the one kind that cannot be two clicks: a stroke *is* the drag,
 * so a click on it is a stray press and produces nothing. Everything else — every
 * two-point shape, and the trade-plan gesture — accepts either.
 */
export function advanceGesture(
  state: DrawGesture,
  event: DrawEvent,
  opts: { freehand: boolean },
): { state: DrawGesture; effect: DrawEffect } {
  if (event.type === "cancel") return { state: IDLE, effect: NOTHING };

  switch (state.phase) {
    case "idle":
      // A press always opens a drag. Which gesture it turns out to be is not
      // known until the release, and guessing earlier would mean either no
      // rubber band until the pointer moved or a phantom one after a stray click.
      return event.type === "down"
        ? { state: { phase: "drag", start: event.at, path: [event.at] }, effect: NOTHING }
        : { state, effect: NOTHING };

    case "drag": {
      if (event.type === "move") {
        // Every point is kept during a freehand stroke and simplified once at the
        // end — see `simplifyPath`.
        const path = opts.freehand ? [...state.path, event.at] : state.path;
        return {
          state: { ...state, path },
          effect: { do: "preview", start: state.start, current: event.at, path },
        };
      }
      if (event.type === "up") {
        // **The release decides.** A press that did not travel is a click, and a
        // click leaves the first point standing rather than committing a drawing
        // of no size.
        if (event.click && !opts.freehand) {
          return {
            state: { phase: "place", start: state.start },
            // Keep the band on screen, now anchored and following the pointer.
            effect: { do: "preview", start: state.start, current: event.at, path: state.path },
          };
        }
        return {
          state: IDLE,
          effect: { do: "commit", start: state.start, end: event.at, path: state.path },
        };
      }
      // A second `down` while a button is already held: ignore rather than
      // restart, since a chorded click is not a new drawing.
      return { state, effect: NOTHING };
    }

    case "place": {
      // No button is held, so the band tracks a bare move.
      if (event.type === "move") {
        return {
          state,
          effect: { do: "preview", start: state.start, current: event.at, path: [] },
        };
      }
      // **Committed on the second `down`, not its `up`.** The pointer can travel
      // between the two, and taking the `up` would move the end point by however
      // far the hand drifted while the button was going down.
      if (event.type === "down") {
        return {
          state: IDLE,
          effect: { do: "commit", start: state.start, end: event.at, path: [] },
        };
      }
      // The `up` that follows that `down`, and the `up` of the *first* click if
      // it arrives late. Neither is a second point.
      return { state, effect: NOTHING };
    }
  }
}
